// src/App.tsx (UPDATED WITH PHANTOM SIGNING REDIRECT ROUTE + AIRDROP)
import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { WalletProvider } from './contexts/WalletContext';

// Layout / Sections / Components
import Header from './components/Header';
import HeroSection from './components/HeroSection';
import FeaturesSection from './components/FeaturesSection';
import AdvancedTools from './components/AdvancedTools';
import BundlerWalletManager from './components/BundlerWalletManager';
import BundlerTokenPortfolioManager from './components/BundlerTokenPortfolioManager';
import BundlerJupiterPurchase from './components/BundlerJupiterPurchase';
import RevShareSection from './components/RevShareSection';
import AnalyticsDashboard from './components/AnalyticsDashboard';
import Footer from './components/Footer';
import About from './components/About';
import Tokenomics from './components/Tokenomics';
import TokenCreationForm from './components/TokenCreationForm';
import AdminPanel from './components/AdminPanel';
import SystemTest from './components/SystemTest';
import PumpfunCreationForm from './components/PumpfunCreationForm';
import RaydiumCreationForm from './components/RaydiumCreationForm';
import PhantomMobileTest from './components/PhantomMobileTest';
import Airdrop from './components/Airdrop'; 
import ProductionTokenSniper from './components/ProductionTokenSniper';


// Pages
import CreateTokenPage from './pages/CreateTokenPage';
import DashboardPage from './pages/DashboardPage';
import PhantomSigningRedirect from './pages/PhantomSigningRedirect';

// Migration Tool - Import from components
import MigrationTool from './components/MigrationTool';

// Services
import { storageService } from './services/frontend-bundler/storage.service';
import { bundlerService } from './services/frontend-bundler/bundler.service';
import phantomDeeplinkService from './services/phantom-deeplink.service';

// ---------- Inline Pages for Routing ----------
const HomePage: React.FC = () => {
  return (
    <div>
      <HeroSection />
      <About />
      <Tokenomics />
      <FeaturesSection />
      <RevShareSection />
    </div>
  );
};



const ToolsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'bundler' | 'airdrop' | 'sniper' | 'portfolio'>('bundler');
  return (
    <div className="space-y-4 sm:space-y-8">
      <div className="px-4 sm:px-0">
        <h1 className="text-2xl sm:text-3xl font-bold mb-2">Advanced Tools</h1>
        <p className="text-sm sm:text-base text-gray-400 mb-4 sm:mb-6">
          Secure client-side bundler, token distribution, and sniping tools
        </p>
      </div>
      
      {/* Tab Navigation - Responsive without scroll */}
      <div className="flex space-x-0.5 sm:space-x-1 bg-gray-800/50 p-0.5 sm:p-1 rounded-lg sm:max-w-3xl">
        <button
          onClick={() => setActiveTab('bundler')}
          className={`flex-1 px-2 sm:px-4 py-1.5 sm:py-2 rounded-md sm:rounded-lg font-medium text-xs sm:text-base transition-all ${
            activeTab === 'bundler'
              ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          Bundler
        </button>
        <button
          onClick={() => setActiveTab('airdrop')}
          className={`flex-1 px-2 sm:px-4 py-1.5 sm:py-2 rounded-md sm:rounded-lg font-medium text-xs sm:text-base transition-all ${
            activeTab === 'airdrop'
              ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          Airdrop
        </button>
        <button
          onClick={() => setActiveTab('sniper')}
          className={`flex-1 px-2 sm:px-4 py-1.5 sm:py-2 rounded-md sm:rounded-lg font-medium text-xs sm:text-base transition-all ${
            activeTab === 'sniper'
              ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          Sniper
        </button>
        <button
          onClick={() => setActiveTab('portfolio')}
          className={`flex-1 px-2 sm:px-4 py-1.5 sm:py-2 rounded-md sm:rounded-lg font-medium text-xs sm:text-base transition-all ${
            activeTab === 'portfolio'
              ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          Portfolio
        </button>
      </div>
      
      {/* Tab Content */}
      <div className="px-2 sm:px-0">
        {activeTab === 'bundler' && <AdvancedTools />}
        {activeTab === 'airdrop' && <Airdrop />}
        {activeTab === 'sniper' && <ProductionTokenSniper />}
        {activeTab === 'portfolio' && <BundlerTokenPortfolioManager />}
      </div>
    </div>
  );
};

// NEW: Standalone Airdrop Page
const AirdropPage: React.FC = () => {
  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold mb-2">Token Airdrop Tool</h1>
        <p className="text-gray-400 mb-6">
          Efficiently distribute tokens to multiple wallets with automatic liquidity checking
        </p>
      </div>
      <Airdrop />
    </div>
  );
};

const MyBundlesPage: React.FC = () => {
  return (
    <div className="space-y-8">
      <h1 className="text-3xl font-bold">My Bundles</h1>
      <BundlerWalletManager />
      <BundlerTokenPortfolioManager />
    </div>
  );
};

// Standalone pages for components that deserve their own routes
const AboutPage: React.FC = () => (
  <div className="space-y-8">
    <h1 className="text-3xl font-bold">About</h1>
    <About />
  </div>
);

const TokenomicsPage: React.FC = () => (
  <div className="space-y-8">
    <h1 className="text-3xl font-bold">Tokenomics</h1>
    <Tokenomics />
  </div>
);

const TokenCreationFormPage: React.FC = () => (
  <div className="space-y-8">
    <h1 className="text-3xl font-bold">Token Creation Form</h1>
    <TokenCreationForm />
  </div>
);

const PumpfunCreatePage: React.FC = () => (
  <div className="space-y-8">
    <h1 className="text-3xl font-bold">Create Token (Pumpfun)</h1>
    <PumpfunCreationForm />
  </div>
);

const RaydiumCreatePage: React.FC = () => (
  <div className="space-y-8">
    <h1 className="text-3xl font-bold">Create Token (Raydium)</h1>
    <RaydiumCreationForm />
  </div>
);

const AdminPage: React.FC = () => (
  <div className="space-y-8">
    <h1 className="text-3xl font-bold">Admin Panel</h1>
    <AdminPanel />
  </div>
);

const SystemTestPage: React.FC = () => (
  <div className="space-y-8">
    <h1 className="text-3xl font-bold">System Test</h1>
    <SystemTest />
  </div>
);

const NotFoundPage: React.FC = () => (
  <div className="py-24 text-center">
    <h1 className="text-3xl font-bold mb-2">404 — Page Not Found</h1>
    <p className="text-gray-400">The page you're looking for doesn't exist.</p>
  </div>
);

// ---------- App Root ----------
const App: React.FC = () => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [initError, setInitError] = useState<string | null>(null);

  useEffect(() => {
    initializeApp();
  }, []);

  // Handle Phantom redirect automatically
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const hasPhantomData = urlParams.get('phantom_encryption_public_key') && 
                           urlParams.get('data') && 
                           urlParams.get('nonce');
    
    if (hasPhantomData) {
      console.log('Auto-processing Phantom redirect...');
      phantomDeeplinkService.handlePhantomRedirect()
        .then(publicKey => {
          console.log('Connection successful:', publicKey);
          // Clean up URL parameters after successful connection
          const cleanUrl = window.location.pathname;
          window.history.replaceState({}, document.title, cleanUrl);
          // You might want to trigger a wallet context update here
          // or dispatch an event that the WalletContext can listen to
        })
        .catch(error => {
          console.error('Connection failed:', error);
          // COMMENTED OUT FOR DEBUGGING - DON'T OVERRIDE URL ON ERROR
          // const errorUrl = `${window.location.pathname}?phantom_error=true`;
          // window.history.replaceState({}, document.title, errorUrl);
        });
    }
  }, []);

  const initializeApp = async () => {
    try {
      // Initialize storage service
      await storageService.initialize();

      // Check RPC connection
      const isConnected = await bundlerService.checkConnection();
      if (!isConnected) {
        console.warn('RPC connection failed, using backup');
      }

      setIsInitialized(true);
    } catch (error: any) {
      console.error('App initialization error:', error);
      setInitError(error.message);
      setIsInitialized(true); // Still show app even if init fails
    }
  };

  if (!isInitialized) {
    return (
      <div className="min-h-screen flex flex-col bg-gradient-to-br from-gray-900 via-purple-900/20 to-gray-900 text-white">
        <div className="flex items-center justify-center flex-1">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500 mx-auto mb-4"></div>
            <p className="text-gray-400">Initializing secure storage...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <WalletProvider>
        <div className="min-h-screen flex flex-col bg-gradient-to-br from-gray-900 via-purple-900/20 to-gray-900 text-white">
          
          {/* Migration Tool - Shows only if migration needed */}
          <MigrationTool />

          {/* Show initialization error if any */}
          {initError && (
            <div className="bg-red-900/50 border border-red-600 text-red-200 p-3 text-center">
              <p>⚠️ Initialization Warning: {initError}</p>
            </div>
          )}

          <Header />
          
          <main className="container mx-auto px-4 py-8">
            <Routes>
              {/* Main routes */}
              <Route path="/" element={<HomePage />} />
              <Route path="/tools" element={<ToolsPage />} />
              <Route path="/bundles" element={<MyBundlesPage />} />
              
              {/* NEW: Dedicated Airdrop Route */}
              <Route path="/airdrop" element={<AirdropPage />} />
              
              {/* Analytics & Token creation pages */}
              <Route path="/analytics" element={<AnalyticsDashboard />} />
              <Route path="/create-token" element={<CreateTokenPage />} />
              <Route path="/token/create" element={<TokenCreationFormPage />} />
              <Route path="/token/pumpfun" element={<PumpfunCreatePage />} />
              <Route path="/token/raydium" element={<RaydiumCreatePage />} />
              <Route path="/phantom-test" element={<PhantomMobileTest />} />
              
              {/* Content pages */}
              <Route path="/about" element={<AboutPage />} />
              <Route path="/tokenomics" element={<TokenomicsPage />} />

              {/* Admin / Diagnostics */}
              <Route path="/admin" element={<AdminPage />} />
              <Route path="/system-test" element={<SystemTestPage />} />
              <Route path="/dashboard" element={<DashboardPage />} />

              {/* Phantom signing redirect handler */}
              <Route path="/phantom-signing-redirect" element={<PhantomSigningRedirect />} />

              {/* 404 */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </main>

          <Footer />
        </div>
      </WalletProvider>
    </Router>
  );
};

export default App;